import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import api from '../../api';

/**
 * JugeBoxeQrPrint
 *
 * Printable page showing QR codes for every student in a class.
 * URL: /apps/juge-boxe/qr-print?classeId=<id>&nom=<className>
 *
 * Calls POST /jugeboxe/qr/classe to generate (or rotate) all tokens,
 * then renders a print-optimised grid.
 */
export default function JugeBoxeQrPrint() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const classeId = params.get('classeId') || '';
  const classeNom = params.get('nom') || 'Classe';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [eleves, setEleves] = useState([]); // [{ eleveId, nom, prenom, url }]

  useEffect(() => {
    if (!classeId) {
      setError('Identifiant de classe manquant.');
      return;
    }
    setLoading(true);
    api.post('/jugeboxe/qr/classe', { classeId }, { useTeacherAuth: true })
      .then(res => {
        setEleves(Array.isArray(res.data) ? res.data : []);
      })
      .catch(err => {
        setError(err?.response?.data?.error || err?.message || 'Erreur lors de la génération des QR codes.');
      })
      .finally(() => setLoading(false));
  }, [classeId]);

  return (
    <>
      {/* ── Print styles injected inline ── */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
          .qr-grid {
            display: grid !important;
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 24px !important;
            padding: 16px !important;
          }
          .qr-card {
            border: 1px solid #ccc !important;
            border-radius: 8px !important;
            padding: 12px !important;
            text-align: center !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
        }
        @media screen {
          .qr-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
            gap: 20px;
            padding: 16px;
          }
          .qr-card {
            border: 1px solid #ddd;
            border-radius: 8px;
            padding: 12px;
            text-align: center;
            background: #fff;
          }
        }
      `}</style>

      {/* ── Toolbar (hidden when printing) ── */}
      <Box
        className="no-print"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 3,
          py: 2,
          bgcolor: '#1c1c2b',
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          size="small"
          onClick={() => navigate('/apps/juge-boxe/qr-manager')}
          sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }}
        >
          Retour
        </Button>

        <Typography sx={{ color: '#fff', fontWeight: 700, flex: 1, fontFamily: 'Montserrat, sans-serif' }}>
          QR codes — {classeNom}
        </Typography>

        <Button
          startIcon={<PrintIcon />}
          variant="contained"
          onClick={() => window.print()}
          disabled={loading || eleves.length === 0}
          sx={{ bgcolor: '#a51c30', '&:hover': { bgcolor: '#8b1526' } }}
        >
          Imprimer
        </Button>
      </Box>

      {/* ── Content ── */}
      <Box sx={{ bgcolor: '#f5f5f5', minHeight: 'calc(100vh - 56px)', py: 2 }}>
        {loading && (
          <Box className="no-print" sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box className="no-print" sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {!loading && !error && eleves.length === 0 && (
          <Box className="no-print" sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="text.secondary">Aucun élève trouvé dans cette classe.</Typography>
          </Box>
        )}

        {!loading && eleves.length > 0 && (
          <>
            {/* Print header (visible in print only) */}
            <Box
              sx={{
                display: 'none',
                '@media print': { display: 'block', textAlign: 'center', mb: 2, fontFamily: 'Arial, sans-serif' },
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                JugeBoxe — QR codes élèves
              </Typography>
              <Typography variant="body2" sx={{ color: '#555' }}>
                Classe : {classeNom} — {eleves.length} élève{eleves.length > 1 ? 's' : ''}
              </Typography>
            </Box>

            <div className="qr-grid">
              {eleves.map((e) => (
                <div className="qr-card" key={e.eleveId}>
                  <QRCodeSVG value={e.url} size={160} />
                  <div style={{ marginTop: 8, fontWeight: 700, fontSize: 14, fontFamily: 'Arial, sans-serif' }}>
                    {e.prenom} {e.nom}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Box>
    </>
  );
}
